import VueRouter, { Route } from 'vue-router'
import { Logger } from '@vue-storefront/core/lib/logger'

const CHUNK_ERROR_NAME = 'ChunkLoadError'
const CHUNK_ERROR_MESSAGE_PATTERN = /Loading (?:CSS )?chunk [\w-]+ failed/i
const RELOAD_DEBOUNCE_MS = 10000
const LAST_RELOAD_STORAGE_KEY = 'vsf-chunk-error-reloaded-at'

/**
 * @param {Error} error
 * @returns {boolean}
 */
function isChunkLoadError (error: Error): boolean {
  if (!error) return false
  return error.name === CHUNK_ERROR_NAME || CHUNK_ERROR_MESSAGE_PATTERN.test(error.message || '')
}

/**
 * @returns {boolean}
 */
function reloadedRecently (): boolean {
  try {
    const lastReloadedAt = Number(window.sessionStorage.getItem(LAST_RELOAD_STORAGE_KEY))
    return !!lastReloadedAt && (Date.now() - lastReloadedAt) < RELOAD_DEBOUNCE_MS
  } catch (storageError) {
    return false
  }
}

/**
 * @returns {void}
 */
function markReloaded (): void {
  try {
    window.sessionStorage.setItem(LAST_RELOAD_STORAGE_KEY, String(Date.now()))
  } catch (storageError) {
    Logger.debug('Could not persist chunk-reload timestamp', 'chunk-error', storageError)()
  }
}

/**
 * @param {VueRouter} router
 * @returns {void}
 */
export function registerChunkErrorHandler (router: VueRouter): void {
  router.onError((error: Error) => {
    if (!isChunkLoadError(error)) {
      Logger.error('Unhandled router navigation error', 'router', error)()
      return
    }

    if (reloadedRecently()) {
      Logger.error('Chunk failed to load again shortly after a reload - skipping to avoid a reload loop', 'chunk-error', error)()
      return
    }

    const pendingRoute: Route = (router as any).history && (router as any).history.pending
    const targetPath = pendingRoute && pendingRoute.fullPath

    Logger.warn('Reloading to recover from a chunk load failure (likely a stale deploy)', 'chunk-error', error)()
    markReloaded()

    if (targetPath) {
      window.location.assign(targetPath)
    } else {
      window.location.reload()
    }
  })
}
